/**
 * Reset Password Form component for trAInedup.ai
 * Allows users to set a new password after following the reset email link
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, Mail, ArrowLeft } from 'lucide-react';
import { Card } from '../common/Card';
import { Input } from '../common/Input';
import { Button, ButtonLink } from '../common/Button';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../utils/constants';

export const ResetPasswordForm: React.FC = () => {
  const { verifyPasswordResetCode, confirmPasswordReset } = useAuth();
  const [searchParams] = useSearchParams();
  const oobCode = useMemo(() => searchParams.get('oobCode'), [searchParams]);
  const requestedReturnTo = useMemo(() => searchParams.get('returnTo') || '', [searchParams]);
  const safeReturnTo = useMemo(
    () => (requestedReturnTo.startsWith('/') ? requestedReturnTo : ''),
    [requestedReturnTo]
  );
  const loginPath = safeReturnTo || ROUTES.login;
  const forgotPasswordPath = `${ROUTES.forgotPassword}${safeReturnTo ? `?returnTo=${encodeURIComponent(safeReturnTo)}` : ''}`;

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<'verifying' | 'ready' | 'invalid' | 'missing' | 'success'>('verifying');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setStatus('missing');
        return;
      }

      setStatus('verifying');
      setError('');

      const result = await verifyPasswordResetCode(oobCode);
      if (result.success && result.email) {
        setEmail(result.email);
        setStatus('ready');
      } else {
        setError(result.error || 'This reset link is invalid or has expired.');
        setStatus('invalid');
      }
    };

    verifyCode();
  }, [oobCode, verifyPasswordResetCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!oobCode) {
      setError('Missing reset code. Please use the link in your email.');
      setStatus('missing');
      return;
    }

    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const result = await confirmPasswordReset(oobCode, newPassword);
    setIsSubmitting(false);

    if (result.success) {
      setStatus('success');
    } else {
      setError(result.error || 'Unable to reset password. Please try again.');
    }
  };

  const renderContent = () => {
    if (status === 'verifying') {
      return <p className="text-sm text-neutral-600">Verifying your reset link...</p>;
    }

    if (status === 'missing') {
      return (
        <div className="space-y-4">
          <p className="text-sm text-neutral-700">
            We couldn&apos;t find a reset link. Please return to the forgot password page and request a new email.
          </p>
          <ButtonLink href={forgotPasswordPath} variant="primary" fullWidth>
            Request a new reset email
          </ButtonLink>
        </div>
      );
    }

    if (status === 'invalid') {
      return (
        <div className="space-y-4">
          <p className="text-sm text-red-600">{error || 'This reset link is invalid or has expired.'}</p>
          <ButtonLink href={forgotPasswordPath} variant="primary" fullWidth>
            Request a new reset email
          </ButtonLink>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="space-y-6">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-700 text-sm font-medium">Your password has been reset successfully.</p>
            <p className="text-green-700 text-sm mt-2">You can now sign in with your new password.</p>
          </div>
          <ButtonLink href={loginPath} variant="primary" fullWidth>
            Back to sign in
          </ButtonLink>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {email && (
          <Input label="Email Address" name="email" type="email" value={email} icon={Mail} disabled />
        )}

        <Input
          label="New Password"
          name="newPassword"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          icon={Lock}
          placeholder="Enter a new password"
          required
          disabled={isSubmitting}
        />

        <Input
          label="Confirm New Password"
          name="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          icon={Lock}
          placeholder="Re-enter your new password"
          required
          disabled={isSubmitting}
        />

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting}>
          {isSubmitting ? 'Updating password...' : 'Update password'}
        </Button>
      </form>
    );
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <div className="text-center mb-6">
        <h2 className="font-montserrat font-bold text-2xl text-neutral-800 mb-2">Create a new password</h2>
        <p className="text-neutral-600 text-sm">
          {status === 'success'
            ? 'Your password has been updated. Sign in to continue.'
            : 'Choose a new password to secure your account.'}
        </p>
      </div>

      {renderContent()}

      {status !== 'success' && (
        <div className="mt-6 flex items-center justify-center">
          <ArrowLeft className="w-4 h-4 text-neutral-500 mr-2" />
          <Link
            to={loginPath}
            className="text-sm text-primary hover:text-primary-600 font-medium transition-colors duration-200"
          >
            Back to sign in
          </Link>
        </div>
      )}
    </Card>
  );
};

export default ResetPasswordForm;
